import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { api, transformer } from "~/utils/api"
import {
  createTRPCClient,
  httpBatchStreamLink,
  httpLink,
  httpSubscriptionLink,
  loggerLink,
  splitLink,
} from "@trpc/client"
import { useAuth0 } from "@auth0/auth0-react"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import AuthError from "~/components/reusable/errors/AuthError"
import { type AppRouter } from "~/server/api/root"
import { UnexpectedHtmlResponseError, createBinaryFetch } from "~/utils/binary-fetch"
import { simulatedRoleHeader } from "~/utils/roleSimulation"
import { isMockAuth, mockAuthHeaders } from "~/utils/mockAuth"
import { TAB_ID } from "~/utils/updates/tabId"

/**
 * FNV-1a (Fowler-Noll-Vo) hash function - fast, non-cryptographic hash.
 * Returns base36 string for compact representation.
 *
 * @see https://en.wikipedia.org/wiki/Fowler%E2%80%93Noll%E2%80%93Vo_hash_function
 */
function fnv1aHash(str: string): string {
  let hash = 2166136261 // FNV offset basis
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i)
    hash = (hash * 16777619) >>> 0 // 32-bit overflow
  }
  return hash.toString(36)
}

/**
 * WeakMap cache for query key hashes. Uses WeakMap because:
 * - Keys are arrays (objects), so WeakMap accepts them
 * - Auto garbage-collected when array references are no longer held
 * - No memory leaks, no manual cache eviction needed
 */
const listKeysCache = new WeakMap<readonly unknown[], string>()

/**
 * Custom queryKeyHashFn for React Query. Optimizes the default JSON.stringify
 * behavior by:
 *
 * 1. **Top-level caching**: If same queryKey reference is passed again, return
 *    cached hash immediately (O(1) lookup)
 *
 * 2. **Large array optimization**: Arrays >20 elements are hashed separately
 *    and cached, avoiding repeated serialization of large stable arrays
 *    (e.g., variantIds from Zustand stores)
 *
 * 3. **WeakMap for auto-cleanup**: Cache entries are automatically removed
 *    when array references are garbage collected
 *
 * @see https://tanstack.com/query/latest/docs/reference/QueryClient#queryclient
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/query-keys
 */
function fastQueryKeyHash(queryKey: readonly unknown[]): string {
  const cached = listKeysCache.get(queryKey)
  if (cached) return cached

  const prefix = Array.isArray(queryKey[0]) ? queryKey[0].join(".") : ""

  const hash = fnv1aHash(
    JSON.stringify(queryKey, (_, value) => {
      // Arrays >20 elements: hash separately to avoid re-serializing large stable arrays
      if (Array.isArray(value) && value.length > 20) {
        if (listKeysCache.has(value)) {
          return listKeysCache.get(value)
        }
        const key =
          typeof value[0] !== "object"
            ? `__arr_${fnv1aHash(value.join("\0"))}`
            : `__arr_${fnv1aHash(JSON.stringify(value))}`
        listKeysCache.set(value, key)
        return key
      }
      return value
    }),
  )
  const key = prefix ? `${prefix}:${hash}` : hash
  listKeysCache.set(queryKey, key)
  return key
}

const INTERNAL_ROUTERS = new Set([
  "updatedAt",
  "healthCheck",
  "sqlQuery",
  "bigQuery",
  "forecastValidationRule",
])

interface TrpcProviderProps {
  children: React.ReactNode
}

const sessionStart = new Date().toISOString()
const sessionHeaders = { "x-session-start": sessionStart, "x-tab-id": TAB_ID }
const binaryFetch = createBinaryFetch()

/**
 * Auth0 `error` codes that mean "the user simply isn't logged in yet" rather than
 * "their session is broken". For these we must send them through login, NOT log them
 * out — logging out clears the Auth0 session and re-triggers silent auth, which fails
 * again, producing an infinite /authorize → /v2/logout redirect loop (most visible on
 * fresh/incognito sessions where there is no existing Auth0 session cookie).
 */
const RECOVERABLE_AUTH_ERRORS = new Set([
  "login_required",
  "consent_required",
  "interaction_required",
  "missing_refresh_token",
])

const getAuthErrorCode = (error: unknown): string | undefined =>
  typeof error === "object" && error !== null && "error" in error
    ? String((error as { error: unknown }).error)
    : undefined

// sessionStorage, not a module var, so it survives the loginWithRedirect/logout reload —
// else an authenticated session that can't get a token (e.g. no Auth0 org) loops on /authorize.
const RECOVERY_ATTEMPTED_KEY = "authRecoveryAttempted"

/** Acquire an access token for a tRPC request; recover once per session on failure, else fail fast. */
async function acquireAuthHeaders(
  isAuthenticated: boolean,
  getAccessTokenSilently: ReturnType<typeof useAuth0>["getAccessTokenSilently"],
  loginWithRedirect: ReturnType<typeof useAuth0>["loginWithRedirect"],
  logout: ReturnType<typeof useAuth0>["logout"],
): Promise<Record<string, string>> {
  try {
    if (!isAuthenticated) {
      throw new Error("Failed to get access token")
    }

    const token = await getAccessTokenSilently()
    sessionStorage.removeItem(RECOVERY_ATTEMPTED_KEY)
    return {
      Authorization: `Bearer ${token}`,
      ...sessionHeaders,
      // Staff role simulation — read fresh per request so switching roles
      // applies immediately. Server-validated (downgrade-only, staff JWTs only).
      ...simulatedRoleHeader(),
    }
  } catch (error) {
    // On the login route, the login page itself owns the auth redirect —
    // including invitation acceptance (invitation/organization params). A second,
    // param-less redirect from the data layer races it and lands the user on the
    // plain login screen with the invitation dropped. Fail the request quietly and
    // let /login drive the redirect.
    if (window.location.pathname.startsWith("/login")) {
      throw new Error(`Failed to get access token ${error}`)
    }

    if (sessionStorage.getItem(RECOVERY_ATTEMPTED_KEY)) {
      throw new Error(`Failed to get access token ${error}`)
    }
    sessionStorage.setItem(RECOVERY_ATTEMPTED_KEY, "1")

    const code = getAuthErrorCode(error)
    if (!isAuthenticated || (code && RECOVERABLE_AUTH_ERRORS.has(code))) {
      // Not signed in yet — go log in and return here afterwards.
      console.info("Access token unavailable, redirecting to login:", code ?? error)
      void loginWithRedirect({
        appState: { returnTo: window.location.pathname + window.location.search },
      })
    } else {
      // Genuine session failure — log out.
      console.error("Error getting access token:", error)
      logout({ logoutParams: { returnTo: window.location.origin } })
    }
    throw new Error(`Failed to get access token ${error}`)
  }
}

// Rate-limited (not cleared) so a broken edge slow-bounces instead of tight-looping; survives
// the redirect reload. Clearing on token success would loop: SSO succeeds instantly → cleared.
const HTML_REAUTH_AT_KEY = "htmlResponseReauthAt"
const HTML_REAUTH_MIN_INTERVAL_MS = 10 * 60 * 1000

// Singleton QueryClient can't close over the current loginWithRedirect; bridge via an effect.
let latestLoginWithRedirect: ReturnType<typeof useAuth0>["loginWithRedirect"] | undefined

// tRPC wraps the transport error as `.cause`; unwrap from both.
const getHtmlResponseError = (error: unknown): UnexpectedHtmlResponseError | undefined => {
  if (error instanceof UnexpectedHtmlResponseError) return error
  const cause = (error as { cause?: unknown })?.cause
  return cause instanceof UnexpectedHtmlResponseError ? cause : undefined
}

function routeHtmlResponseToReauth(error: unknown) {
  const htmlError = getHtmlResponseError(error)
  if (!htmlError || !latestLoginWithRedirect) return
  if (htmlError.status >= 500) return // gateway/outage page, not session expiry

  const lastAt = Number(sessionStorage.getItem(HTML_REAUTH_AT_KEY)) || 0
  if (Date.now() - lastAt < HTML_REAUTH_MIN_INTERVAL_MS) return
  sessionStorage.setItem(HTML_REAUTH_AT_KEY, String(Date.now()))
  console.info("tRPC received HTML instead of data. Session likely expired, re-authenticating")
  void latestLoginWithRedirect({
    appState: { returnTo: window.location.pathname + window.location.search },
  })
}

export function TrpcProvider({ children }: TrpcProviderProps) {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    logout,
    loginWithRedirect,
    user,
    error,
  } = useAuth0()
  const isTestMode = isMockAuth()

  useEffect(() => {
    latestLoginWithRedirect = loginWithRedirect
  }, [loginWithRedirect])

  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (err) => routeHtmlResponseToReauth(err),
        }),
        mutationCache: new MutationCache({
          onError: (err) => routeHtmlResponseToReauth(err),
        }),
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: (query) => {
              const path = query.queryKey[0]
              if (Array.isArray(path) && INTERNAL_ROUTERS.has(path[0] as string)) {
                return "always"
              }
              return false
            },
            refetchOnMount: true,
            // Must stay long: demand-2's engine fan-out inherits it and refetches on mount, and every
            // planner edit already invalidates explicitly (useInvalidateDemandReads).
            staleTime: 60 * 60 * 1000,
            queryKeyHashFn: fastQueryKeyHash,
          },
        },
      }),
  )

  const trpcClient = useMemo(
    () =>
      createTRPCClient<AppRouter>({
        links: [
          loggerLink({
            enabled: () => false,
          }),
          splitLink({
            // chat.* uses streaming queries (async generator .query) — needs httpBatchStreamLink.
            // Subscriptions (e.g. chat.queryHelperStream) fall through to the inner split below.
            condition: (op) => op.path.startsWith("chat.") && op.type !== "subscription",
            true: httpBatchStreamLink({
              url: `/trpc`,
              transformer,
              async headers() {
                if (isTestMode) {
                  return {
                    ...mockAuthHeaders(),
                    ...sessionHeaders,
                    ...simulatedRoleHeader(),
                  }
                }

                return acquireAuthHeaders(
                  isAuthenticated,
                  getAccessTokenSilently,
                  loginWithRedirect,
                  logout,
                )
              },
              methodOverride: "POST",
            }),
            false: splitLink({
              // haki.chat is a tRPC subscription (SSE) — uses httpSubscriptionLink with connectionParams for auth
              condition: (op) => op.type === "subscription",
              true: httpSubscriptionLink({
                url: `/trpc`,
                transformer,
                connectionParams: async () => {
                  if (isTestMode) {
                    return {
                      ...mockAuthHeaders(),
                      ...sessionHeaders,
                      ...simulatedRoleHeader(),
                    }
                  }

                  return acquireAuthHeaders(
                    isAuthenticated,
                    getAccessTokenSilently,
                    loginWithRedirect,
                    logout,
                  )
                },
              }),
              false: httpLink({
                url: `/trpc`,
                transformer,
                fetch: binaryFetch,
                async headers() {
                  if (isTestMode) {
                    return {
                      ...mockAuthHeaders(),
                      ...sessionHeaders,
                      ...simulatedRoleHeader(),
                    }
                  }

                  return acquireAuthHeaders(
                    isAuthenticated,
                    getAccessTokenSilently,
                    loginWithRedirect,
                    logout,
                  )
                },
                methodOverride: "POST",
              }),
            }),
          }),
        ],
      }),
    [isAuthenticated, getAccessTokenSilently, isTestMode, logout, loginWithRedirect],
  )

  if (isLoading) return <LoadingOverlay size="lg" height="h-[100vh]" messages="Authenticating..." />

  // Auth0 rejected the login (e.g. the app requires org membership and the user has none,
  // returning ?error=invalid_request). The SDK doesn't reliably surface this as `error`, so
  // also read it off the URL. Show it instead of bouncing to /login, which loops forever.
  // RefreshAccessTokenError keeps its own silent-retry path (handled in _auth).
  const urlParams =
    typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null
  const urlAuthError = urlParams?.get("error")
  if (urlAuthError || (error && error.name !== "RefreshAccessTokenError")) {
    const description = urlParams?.get("error_description")
    return (
      <AuthError
        error={
          description ? new Error(description) : (error ?? new Error("Authentication failed."))
        }
      />
    )
  }

  // Authenticated but in no organization: stop above the data-fetching tree (a router guard
  // can't — providers like UpdatesProvider mount higher) so no request fires and loops.
  if (isAuthenticated && user && !user.org_id) {
    return <AuthError error={new Error("No organization is associated with this account.")} />
  }

  return (
    <api.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </api.Provider>
  )
}
